<!--
  `#/home/start` — the panel that gets a new annotator to their first correct box, and the
  landing page's default tab since 2026-08-09.

  It says the things that actually cost the first session time, rather than restating
  `docs/annotation_guidelines.md`. The single biggest friction point in the first real session
  was the annotator id: without one, `canDraw` is false and the image opens read-only because a
  lock cannot be claimed, and nothing on screen said so. Its *status* is still the first thing
  here.

  Its **rationale** is not, any more. This panel used to open with four paragraphs on why
  provenance is a non-nullable column and why no request may carry an `annotator_id` — all of
  it true, none of it what somebody who has just arrived needs in order to draw a box. Reading
  order was: a wall of justification, then the three-step loop, then what to open. It is now
  the loop, what to open, and the justification last and folded.

  One conditional: when the session genuinely **cannot** write, the explanation stops being
  background and becomes the reason nothing works, so it renders open. Folded is for when the
  thing it explains is already true.
-->
<script lang="ts">
  import { href } from '../lib/router.svelte';
  import { annotator } from '../lib/stores/annotator.svelte';
  import { auth } from '../lib/stores/auth.svelte';

  const GUIDELINES = 'tools/hismaic_annotator/docs/annotation_guidelines.md';
</script>

<div class="col panels">
  <!-- Status only. One line, and it is a check, not a lesson. -->
  <section class="panel col" aria-labelledby="identity-heading" data-section="identity">
    {#if annotator.ready}
      <p class="small ok-line" id="identity-heading">
        <span class="badge small mono">Signed in</span>
        You are annotating as <strong>{auth.displayName}</strong>
        <span class="muted mono">({annotator.id})</span>. Every box you draw will carry it.
      </p>
    {:else if annotator.readOnlyAccount}
      <p class="small warn-line" id="identity-heading">
        <span class="badge warn small mono">Viewer</span>
        You can read everything here and change nothing. Ask an administrator if you need to
        annotate.
      </p>
    {:else}
      <p class="small warn-line" id="identity-heading">
        <span class="badge warn small mono">Not signed in</span>
        Sign in before boxing anything — drawing is disabled without an identity, and every
        image opens read-only.
      </p>
    {/if}
  </section>

  <!-- ---------------------------------------------------------------- -->
  <section class="panel col" aria-labelledby="loop-heading" data-section="loop">
    <h2 id="loop-heading">The loop</h2>
    <ol class="loop small">
      <li>
        <strong>Draw a box</strong> round one glyph (<kbd>D</kbd> for draw mode, then drag).
      </li>
      <li>
        <strong>The chip under the cursor is assigned to it</strong> — the expected letter from
        OCIANA's reading, with its <span class="mono">{'{uncertain}'}</span> and
        <span class="mono">[restored]</span> flags already attached.
      </li>
      <li><strong>The cursor advances</strong> to the next chip.</li>
    </ol>
    <p class="small muted note">
      There is no typing and no menu in that loop. Clicking a chip only moves the cursor —
      changing a box's letter is always deliberate (<kbd>L</kbd>, shift-click or right-click),
      because a letter identity must never change as a side effect of navigating. The whole
      workflow is measured in letters boxed per minute, which is why it is keyboard-first.
    </p>
  </section>

  <!-- ---------------------------------------------------------------- -->
  <section class="panel col" aria-labelledby="first-heading" data-section="first-image">
    <h2 id="first-heading">What to open first</h2>
    <ol class="loop small">
      <li>
        Set the id above, then open the
        <a href={href({ name: 'queue', task: null })}>work queue</a> and pick a task. Each one
        works the <strong>seed batch</strong> — inscriptions sampled to cover every vocabulary
        class, so a box drawn there is worth more than one drawn at random. There is nothing to
        filter: the queue hands you what is left, in order.
      </li>
      <li>
        Prefer a <strong>short</strong> inscription for the first one. Five chips resolved end to
        end teaches the loop; forty chips on a weathered panel teaches frustration.
      </li>
      <li>
        Read the <a href={href({ name: 'home', tab: 'conventions' })}>Conventions</a> panel before
        the first lacuna chip rather than after — the hyphen and the sticky
        <span class="mono">illegible</span> chip are the two things that get recorded wrongly, and
        wrong boxes cost more to find than to avoid.
      </li>
    </ol>
    <p class="small muted note">
      Nothing you do is destructive: autosave writes the whole box set for the image, an image can
      be skipped (<kbd>Shift</kbd>+<kbd>S</kbd>) without completing it, and a lock is yours until
      you leave. The full conventions are
      <a href={href({ name: 'guidelines', at: null })}>here in the app</a> — the same
      <span class="mono">{GUIDELINES}</span> the repository carries, served rather than cited.
    </p>
  </section>

  <!-- ---------------------------------------------------------------- -->
  <details class="panel identity-why" data-section="identity-why" open={!annotator.ready}>
    <summary><h2 id="identity-heading-why">Why the identity is not yours to choose</h2></summary>
    <p class="small">
      Signed out, drawing is disabled (<span class="mono">canDraw</span> requires an identity)
      and every image opens <strong>read-only</strong>, because the single-writer lock is
      claimed in your name and there is no name to claim it with. A <strong>viewer</strong>
      account is in the same position, by design rather than by accident.
    </p>
    <p class="small">
      The identity is <strong>not yours to choose</strong>, and that is the point. It used to be
      a label this browser stored and sent with every write, which meant a box's author was
      whatever the client said it was. It is now derived from your sign-in by the service, and
      no request can carry an <span class="mono">annotator_id</span> at all — so nobody can
      attribute work to somebody who did not do it, including by accident.
    </p>
    <p class="small">
      The reason provenance is mandatory rather than convenient:
      <span class="mono">annotator_id</span> is a non-nullable column on every stored box,
      alongside <span class="mono">source</span> (human / model / model_corrected) and the
      timestamps. That is what keeps "does this error correlate with the data it was trained on
      — or with who labelled it?" answerable later. A box with no author is a box nobody can
      audit.
    </p>
    <p class="small">
      Accounts are <strong>invite-only</strong>: an administrator issues a one-time link, and
      the role on it (<span class="mono">admin</span>, <span class="mono">annotator</span> or
      <span class="mono">viewer</span>) is fixed by the invitation rather than chosen by whoever
      redeems it.
    </p>
  </details>
</div>

<style>
  .panels {
    gap: 12px;
  }

  p {
    margin: 0;
    max-width: 82ch;
  }

  .note {
    margin: 0;
  }

  .badge {
    display: inline-block;
    border: 1px solid var(--ok);
    color: var(--ok);
    border-radius: 4px;
    padding: 0 6px;
  }

  .badge.warn {
    border-color: var(--warn);
    color: var(--warn);
  }

  .ok-line,
  .warn-line {
    display: flex;
    gap: 8px;
    align-items: baseline;
    flex-wrap: wrap;
  }

  .loop {
    margin: 0;
    padding-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-width: 82ch;
  }

  /* An `<h2>` inside the summary so the heading stays in the document outline whether the
     disclosure is open or shut — a screen-reader user should be able to find this section by
     heading, not only by stumbling onto a summary. `display: inline` keeps it on the marker's
     line instead of dropping below it. */
  .identity-why summary {
    cursor: pointer;
  }

  .identity-why summary h2 {
    display: inline;
    margin: 0;
  }

  .identity-why[open] summary {
    margin-bottom: 10px;
  }

  .identity-why p + p {
    margin-top: 8px;
  }
</style>
